import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fmtNum, fmtMoneyCur, PRODUCTO_COLORS } from "../lib/format";
import { Card } from "../components/shared";
import { Button } from "../components/ui/button";
import { Map as MapIcon, Table2 } from "lucide-react";

export default function Desarrollo() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const [productos, setProductos] = useState(null);
  const [vista, setVista] = useState("tabla");
  useEffect(() => { api.get(`/properties/${pid}/productos`).then((r) => setProductos(r.data)); }, [pid]);

  const totales = (productos || []).reduce((a, p) => ({
    unidades: a.unidades + p.unidades_definidas, autorizadas: a.autorizadas + p.unidades_autorizadas,
    m2: a.m2 + p.m2_totales_calc, costo: a.costo + p.costo_total, ingreso: a.ingreso + p.ingreso_estimado,
  }), { unidades: 0, autorizadas: 0, m2: 0, costo: 0, ingreso: 0 });

  return (
    <div className="p-4 sm:p-8">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-5">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Módulo Desarrollo</p>
          <h1 className="font-display text-3xl font-bold text-[#002B49]">Portafolio de productos por UPE</h1>
        </div>
        <Button data-testid="desarrollo-toggle-vista" variant="outline" onClick={() => setVista(vista === "tabla" ? "mapa" : "tabla")} className="h-9 gap-2 border-[#002B49] text-[#002B49]">
          {vista === "tabla" ? <><MapIcon size={14} /> Modo mapa</> : <><Table2 size={14} /> Tabla</>}
        </Button>
      </div>
      {!productos ? <p className="text-slate-400 animate-pulse">Cargando portafolio...</p> : productos.length === 0 ? (
        <Card className="p-10 text-center text-slate-400">Este proyecto aún no tiene UPEs. Créalas en el Mapa Maestro para definir sus productos.</Card>
      ) : vista === "mapa" ? (
        <Card className="p-6">
          <div className="flex flex-wrap gap-2">
            {productos.map((p) => (
              <button key={p.id} data-testid={`dev-map-${p.upe_clave}`} onClick={() => navigate(`/p/${pid}/desarrollo/${p.upe_id}`)}
                title={`${p.upe_clave} · ${p.estatus}`}
                style={{ flex: "1 1 180px", minHeight: 100, background: PRODUCTO_COLORS[p.estatus] || "#CBD5E1" }}
                className="rounded-md border-2 border-white text-left p-2.5 transition-all hover:scale-[1.02] hover:shadow-md">
                <p className="font-display font-bold text-sm text-[#002B49]">{p.upe_clave}</p>
                <p className="text-[11px] text-[#002B49]/80 truncate">{p.nombre_comercial || p.tipo}</p>
                <p className="text-[10px] text-[#002B49]/70 mt-1">{p.estatus}</p>
              </button>))}
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-slate-500 mt-4 pt-4 border-t border-slate-100">
            {Object.entries(PRODUCTO_COLORS).map(([e, c]) => (
              <span key={e} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border border-slate-300" style={{ background: c }} />{e}</span>))}
          </div>
        </Card>
      ) : (
        <Card className="overflow-hidden" testId="comparativo-productos">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[980px]">
              <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3">UPE</th><th className="px-4 py-3">Producto</th><th className="px-4 py-3">Estatus</th>
                <th className="px-4 py-3 text-right">Unidades def./aut.</th><th className="px-4 py-3 text-right">m² a construir</th>
                <th className="px-4 py-3 text-right">Costo estimado</th><th className="px-4 py-3 text-right">Ingreso estimado</th>
                <th className="px-4 py-3 text-right">Margen</th></tr></thead>
              <tbody>
                {productos.map((p) => (
                  <tr key={p.id} data-testid={`producto-row-${p.upe_clave}`} onClick={() => navigate(`/p/${pid}/desarrollo/${p.upe_id}`)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer">
                    <td className="px-4 py-3 font-bold text-[#002B49]">{p.upe_clave}</td>
                    <td className="px-4 py-3 text-slate-700">{p.nombre_comercial || <span className="text-slate-400">Sin nombre · {p.tipo}</span>}
                      {(!p.operador_nombre || p.operador_nombre === "Por definir") && <span className="ml-2 text-[10px] text-amber-600 font-bold">⚠ sin operador</span>}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PRODUCTO_COLORS[p.estatus] }} />{p.estatus}</span></td>
                    <td className={`px-4 py-3 text-right font-semibold ${p.excede_autorizado ? "text-red-600" : "text-slate-700"}`}>
                      {p.unidades_definidas} / {p.unidades_autorizadas}{p.excede_autorizado && " ⚠"}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{fmtNum(Math.round(p.m2_totales_calc))}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{fmtMoneyCur(p.costo_total)}</td>
                    <td className="px-4 py-3 text-right text-slate-600">{fmtMoneyCur(p.ingreso_estimado)}</td>
                    <td className={`px-4 py-3 text-right font-bold ${p.margen_pct > 0 ? "text-emerald-600" : "text-slate-400"}`}>{p.margen_pct}%</td>
                  </tr>))}
                <tr className="bg-slate-50 font-bold text-[#002B49]">
                  <td className="px-4 py-3" colSpan={3}>Totales del proyecto</td>
                  <td className="px-4 py-3 text-right">{totales.unidades} / {totales.autorizadas}</td>
                  <td className="px-4 py-3 text-right">{fmtNum(Math.round(totales.m2))}</td>
                  <td className="px-4 py-3 text-right">{fmtMoneyCur(totales.costo)}</td>
                  <td className="px-4 py-3 text-right">{fmtMoneyCur(totales.ingreso)}</td>
                  <td className="px-4 py-3"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </Card>
      )}
      <p className="text-xs text-slate-400 mt-4">Haz clic en una UPE para abrir su ficha de producto (concepto, mezcla de unidades, amenidades, estándares del operador y presupuesto paramétrico).</p>
    </div>
  );
}
